import {useQuery} from "@tanstack/react-query";
import {getBalanceBonusService} from "../../services/wallet/getBalanceBonus";
import {useAuthStore} from "../../store/useAuth.store";

/**
 * Fetches the current user's balance bonus alongside the regular wallet
 * balance. Reuses the shared authenticated request client (Keycloak bearer
 * token + wallet base URL), mirroring `useGetUserWallet`.
 *
 * The response `data` is generic and may describe different bonus kinds via the
 * `type` field (e.g. "reactivation"). Shape:
 *   { hasBonus, type, currency, withdrawable, status, playableCents,
 *     formattedPlayable, grantedCents, wageredCents, wageringTargetCents,
 *     wageringRemainingCents, winningsLockedCents, wageringMet, expiresAt }
 *
 * IMPORTANT: the bonus is a SEPARATE, non-withdrawable amount and must never be
 * merged into the withdrawable wallet balance.
 *
 * @param {number} [staleTime] - Optional override for the query stale time.
 * @return {{
 *   bonus: object | undefined,
 *   hasBonus: boolean,
 *   isLoading: boolean,
 *   isError: boolean,
 *   error: unknown,
 *   refetch: Function,
 * }}
 */
const useGetBalanceBonus = staleTime => {
  const authStatus = useAuthStore(state => state.authStatus);

  const {
    data: bonus,
    isError,
    isLoading,
    isSuccess,
    refetch,
    error,
  } = useQuery({
    queryKey: ["balanceBonus"],
    queryFn: getBalanceBonusService,
    staleTime: staleTime ?? 5 * (60 * 1000), // 5 mins
    select: res => res?.data?.data,
    enabled: authStatus === "authenticated",
  });

  return {
    bonus,
    hasBonus: Boolean(bonus?.hasBonus),
    isError,
    isLoading,
    isSuccess,
    refetch,
    error,
  };
};

export default useGetBalanceBonus;
